/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { FeelPlaygroundCollapsiblePanel } from 'features/properties-panel-feel-playground';
import { TextAreaAndSubmit } from 'experiments/ai-features/TextAreaAndSubmit';
import { FeelCopilotDiscovery } from 'experiments/ai-features/feel-copilot/FeelCopilotDiscovery';
import { AISparkleBlack } from 'icons';
import { feelCopilotClient } from 'experiments/ai-features/feel-copilot/feel-copilot-client';
import Messages from 'experiments/ai-features/bpmn-copilot/Messages';
import { genericErrorMessage, getInputTooLongMessage } from 'experiments/ai-features/feel-copilot/feel-copilot-text';
import { IntroContainer } from 'experiments/ai-features/feel-copilot/IntroContainer';
import { trackingService } from 'services';

import * as Styled from './FeelCopilotPanel.styled';

const extractMessageContent = (response, request) => {
  if (feelCopilotClient.isResponseSuccessful(response)) {
    return response;
  } else if (feelCopilotClient.wasInputTooLong(response)) {
    return { text: getInputTooLongMessage(request) };
  } else {
    return { text: genericErrorMessage };
  }
};

export const FeelCopilotPanel = ({
  expandedHeight,
  expression,
  context,
  modelerInjector,
  onToggleCollapsed,
  isCollapsed,
  areAiFeaturesEnabled,
  organizationManagementPageUrl,
  hasElevatedOrganizationPermissions,
  onUseExpressionClick,
  onUseContextClick,
  location
}) => {
  const [textAreaContent, setTextAreaContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);

  const messagesContainerRef = useRef(null);
  const preventMessagesScroll = useRef(false);

  useEffect(() => {
    if (!messagesContainerRef.current || preventMessagesScroll.current) {
      preventMessagesScroll.current = false;
      return;
    }

    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }, [messages]);

  const getVariables = useCallback(async (modelerInjector) => {
    const service = modelerInjector?.get('variableResolver');

    return service ? Object.values(await service.getVariables()).flat() : [];
  }, []);

  const cancelFunction = useRef(null);

  const cancel = useCallback(() => {
    setIsProcessing(false);
    if (cancelFunction.current) {
      cancelFunction.current();
    }
  }, []);

  const addMessage = (message) => {
    if (message.type === 'ai') {
      // Allows humans to see both the end of their prompt and the start of the response
      preventMessagesScroll.current = true;
    }
    setMessages((prevMessages) => [...prevMessages, { id: v4(), ...message }]);
  };

  const onSubmit = useCallback(
    async (overrideText) => {
      if (typeof overrideText !== 'string' && textAreaContent.trim().length === 0) {
        return;
      }

      const prompt = typeof overrideText === 'string' ? overrideText : textAreaContent;

      setIsProcessing(true);
      addMessage({ type: 'human', text: prompt });
      setTextAreaContent('');

      let variables = [];
      try {
        variables = await getVariables(modelerInjector);
      } catch (e) {
        console.error('Fetching variables from diagram failed. Continuing with FEEL Copilot call', e);
      }

      try {
        let request = {
          prompt,
          context,
          expression,
          variables
        };
        const { response: responsePromise, cancel } = feelCopilotClient.callFeelCopilot(request);
        trackingService.trackFeelCopilotSubmit({ from: location });

        cancelFunction.current = cancel;
        const response = await responsePromise;

        if (feelCopilotClient.wasResponseCancelled(response)) {
          return;
        }

        const messageContent = extractMessageContent(response, request);

        addMessage({ type: 'ai', ...messageContent });

        setIsProcessing(false);
      } catch (e) {
        const errorContextToLog = typeof e?.getMessage === 'function' ? e.getMessage() : null;
        console.error('Error calling OpenAI or parsing response in FEEL Copilot', errorContextToLog);
        addMessage({ type: 'ai', text: genericErrorMessage });
      }

      setIsProcessing(false);
    },
    [context, expression, modelerInjector, getVariables, textAreaContent, location]
  );

  const onExamplePromptClick = (e) => {
    onSubmit(e.target.textContent);
  };

  return (
    <FeelPlaygroundCollapsiblePanel
      title={'FEEL Copilot'}
      titleIcon={<AISparkleBlack height={'14px'} />}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
      expandedHeight={expandedHeight}
      aria-label={'FEEL Copilot'}
    >
      {!areAiFeaturesEnabled ? (
        <FeelCopilotDiscovery
          organizationManagementPageUrl={organizationManagementPageUrl}
          hasElevatedOrganizationPermissions={hasElevatedOrganizationPermissions}
        />
      ) : (
        <Styled.Container>
          <Styled.Body ref={messagesContainerRef}>
            {messages.length === 0 && (
              <IntroContainer onExamplePromptClick={onExamplePromptClick} expression={expression} />
            )}
            <Messages
              messages={messages}
              isProcessing={isProcessing}
              onUseExpressionClick={onUseExpressionClick}
              onUseContextClick={onUseContextClick}
            />
          </Styled.Body>
          <TextAreaAndSubmit
            placeholderText={'Ask me anything about FEEL expressions'}
            isProcessing={isProcessing}
            text={textAreaContent}
            onChangeText={(text) => setTextAreaContent(text)}
            nextFocusRef={null}
            onCancel={cancel}
            onSubmit={onSubmit}
            heightPixels={40}
            paddingPixels={6}
          />
        </Styled.Container>
      )}
    </FeelPlaygroundCollapsiblePanel>
  );
};
